import { Boxes, ClipboardList, Clock, PackageCheck, ShoppingCart } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type NavChild = {
  label: string;
  to: string;
  /** The permission the child page's API actually requires. */
  perm: string;
  /** Exact-path match (for routes that are a prefix of a sibling). */
  end?: boolean;
};

export type NavGroup = {
  label: string;
  icon: LucideIcon;
  /** Where the group header navigates. Re-pointed to the first VISIBLE child. */
  to: string;
  /** Only for a flat (childless) group. */
  perm?: string;
  end?: boolean;
  children?: NavChild[];
};

/**
 * Cashier navigation, grouped for the sidebar.
 *
 * Each entry is paired with the permission its landing page's API actually
 * requires. Rendering a link the holder can't use is worse than hiding it —
 * they land on the page and every fetch 403s with no explanation. Perms are
 * JWT-baked, so a grant reaches the cashier on their next sign-in.
 */
export const NAV: NavGroup[] = [
  {
    label: 'Sell',
    icon: ShoppingCart,
    to: '/',
    children: [
      { label: 'Checkout', to: '/', perm: 'pos.checkout', end: true },
      { label: 'Sell to Stockist', to: '/stockist', perm: 'pos.checkout', end: true },
    ],
  },
  {
    label: 'Releasing',
    icon: PackageCheck,
    to: '/releasing',
    children: [
      { label: 'Releasing', to: '/releasing', perm: 'pos.release', end: true },
      { label: 'History', to: '/releasing/history', perm: 'pos.release' },
    ],
  },
  {
    label: 'Orders',
    icon: ClipboardList,
    to: '/transactions',
    children: [
      { label: 'Sales Orders', to: '/transactions', perm: 'pos.checkout' },
      { label: 'Orders', to: '/orders', perm: 'pos.orders.view' },
    ],
  },
  {
    label: 'Inventory',
    icon: Boxes,
    to: '/inventory',
    perm: 'pos.checkout',
  },
  {
    label: 'Shift',
    icon: Clock,
    to: '/time-logs',
    children: [
      { label: 'Time Logs', to: '/time-logs', perm: 'pos.checkout' },
      { label: 'Daily Report', to: '/daily-report', perm: 'pos.checkout' },
      { label: 'End Shift', to: '/end-shift', perm: 'pos.checkout' },
    ],
  },
];

export type NavViewer = { isSuper: boolean; perms: string[] };

/**
 * Drop everything the viewer can't reach: invisible children, then any group
 * left with no children. A surviving group is re-pointed at its first VISIBLE
 * child, so clicking a group header never lands on a forbidden page.
 */
export function visibleNav(items: NavGroup[], viewer: NavViewer): NavGroup[] {
  const allowed = (perm: string) => viewer.isSuper || viewer.perms.includes(perm);
  return items.flatMap((group) => {
    if (!group.children) {
      return group.perm && !allowed(group.perm) ? [] : [group];
    }
    const children = group.children.filter((c) => allowed(c.perm));
    if (children.length === 0) return [];
    return [{ ...group, to: children[0]!.to, children }];
  });
}

export function isChildActive(child: { to: string; end?: boolean }, pathname: string): boolean {
  if (child.end) return pathname === child.to;
  return pathname === child.to || pathname.startsWith(`${child.to}/`);
}

export function isGroupActive(group: NavGroup, pathname: string): boolean {
  if (group.children?.length) return group.children.some((c) => isChildActive(c, pathname));
  return isChildActive(group, pathname);
}
